import React from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Tile from 'components/Tile/Tile';
import { InputField, SwitchField } from 'components/form/rhf';
import { useNotifications } from 'modules/notification';
import { M_UPDATE_VP } from 'queries/common';
import {
  GET_INTEGRATION_SETTINGS_VALUES_LIST,
  INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY,
  DELETE_INTEGRATION_SETTINGS_VALUES,
} from 'modules/integrationSettings/api';

const PAYNET_CA_INTEGRATION_NAME = 'PAYNET';
const PAYNET_US_INTEGRATION_NAME = 'EQUIFAX_COMMERCIAL';
const PAYNET_CA_WSDL_URL = 'https://secure.paynetonline.ca/direct/PayNetDirect.asmx?WSDL';

const caSchema = z
  .object({
    enabled: z.boolean(),
    username: z.string().optional(),
    password: z.string().optional(),
  })
  .refine(d => !d.enabled || (!!d.username && !!d.password), {
    message: 'Username and password are required when PayNet Canada is enabled',
    path: ['username'],
  });

const usaSchema = z
  .object({
    enabled: z.boolean(),
    username: z.string().optional(),
    password: z.string().optional(),
    inquiryMemberNumber: z.string().optional(),
  })
  .refine(d => !d.enabled || (!!d.username && !!d.password && !!d.inquiryMemberNumber), {
    message: 'All fields are required when PayNet USA is enabled',
    path: ['username'],
  });

type CanadaForm = z.infer<typeof caSchema>;
type UsaForm = z.infer<typeof usaSchema>;

const getEnabledProviders = (vp: any): string[] => {
  const raw = vp?.settingBusinessCreditReportPullInDealerPortal;
  if (Array.isArray(raw)) return raw.filter(Boolean) as string[];
  if (typeof raw === 'string') return raw.replace(/^{|}$/g, '').split(',').filter(Boolean);
  return [];
};

const getExistingCredentials = (isvList: any[], integrationName: string) => {
  const entry = isvList?.find((isv: any) => isv.integrationName === integrationName);
  return entry?.integrationSettingsValues ?? null;
};

export const PaynetSettingsCard = () => {
  const { showNotification } = useNotifications();
  const account = useSelector((state: any) => state.account);
  const vp = useSelector((state: any) => state.vp);
  const accountId: string = account?.id;
  const vendorProfileId: string = account?.vendorProfileId;

  const enabledProviders = getEnabledProviders(vp);

  const { data: paynetCaData, loading: paynetCaLoading } = useQuery(GET_INTEGRATION_SETTINGS_VALUES_LIST, {
    variables: { input: { integrationName: PAYNET_CA_INTEGRATION_NAME, accountId } },
    skip: !accountId,
    fetchPolicy: 'no-cache',
  });

  const { data: paynetUsData, loading: paynetUsLoading } = useQuery(GET_INTEGRATION_SETTINGS_VALUES_LIST, {
    variables: { input: { integrationName: PAYNET_US_INTEGRATION_NAME, accountId } },
    skip: !accountId,
    fetchPolicy: 'no-cache',
  });

  const existingCaCredentials = getExistingCredentials(
    paynetCaData?.integrationSettingsValuesList,
    PAYNET_CA_INTEGRATION_NAME
  );
  const existingUsCredentials = getExistingCredentials(
    paynetUsData?.integrationSettingsValuesList,
    PAYNET_US_INTEGRATION_NAME
  );

  const caForm = useForm<CanadaForm>({
    resolver: zodResolver(caSchema),
    values: {
      enabled: enabledProviders.includes(PAYNET_CA_INTEGRATION_NAME),
      username: existingCaCredentials?.username ?? '',
      password: existingCaCredentials?.password ?? '',
    },
  });

  const usaForm = useForm<UsaForm>({
    resolver: zodResolver(usaSchema),
    values: {
      enabled: enabledProviders.includes(PAYNET_US_INTEGRATION_NAME),
      username: existingUsCredentials?.username ?? '',
      password: existingUsCredentials?.password ?? '',
      inquiryMemberNumber: existingUsCredentials?.inquiryMemberNumber ?? '',
    },
  });

  const [updateVP, { loading: vpUpdating }] = useMutation(M_UPDATE_VP, {
    context: { authRequired: true },
  });

  const [insertISV, { loading: isvInserting }] = useMutation(INSERT_INTEGRATION_SETTINGS_VALUES_ARRAY, {
    context: { authRequired: true },
  });

  const [deleteISV, { loading: isvDeleting }] = useMutation(DELETE_INTEGRATION_SETTINGS_VALUES, {
    context: { authRequired: true },
  });

  const buildUpdatedProviders = (caEnabled: boolean, usEnabled: boolean): string[] => {
    const others = enabledProviders.filter(p => p !== PAYNET_CA_INTEGRATION_NAME && p !== PAYNET_US_INTEGRATION_NAME);
    return [
      ...others,
      ...(caEnabled ? [PAYNET_CA_INTEGRATION_NAME] : []),
      ...(usEnabled ? [PAYNET_US_INTEGRATION_NAME] : []),
    ];
  };

  const handleSave = async () => {
    const [caValid, usaValid] = await Promise.all([caForm.trigger(), usaForm.trigger()]);
    if (!caValid || !usaValid) return;

    const caData = caForm.getValues();
    const usaData = usaForm.getValues();

    try {
      await updateVP({
        variables: {
          entityId: vendorProfileId,
          fieldsObj: {
            settingBusinessCreditReportPullInDealerPortal: buildUpdatedProviders(caData.enabled, usaData.enabled),
          },
        },
      });

      if (caData.enabled) {
        await insertISV({
          variables: {
            input: [
              {
                accountId,
                integrationName: PAYNET_CA_INTEGRATION_NAME,
                integrationSettingsValues: {
                  wsdlUrl: PAYNET_CA_WSDL_URL,
                  username: caData.username,
                  password: caData.password,
                },
              },
            ],
          },
        });
      } else {
        await deleteISV({ variables: { input: { integrationName: PAYNET_CA_INTEGRATION_NAME, accountId } } });
      }

      if (usaData.enabled) {
        await insertISV({
          variables: {
            input: [
              {
                accountId,
                integrationName: PAYNET_US_INTEGRATION_NAME,
                integrationSettingsValues: {
                  username: usaData.username,
                  password: usaData.password,
                  inquiryMemberNumber: usaData.inquiryMemberNumber,
                },
              },
            ],
          },
        });
      } else {
        await deleteISV({ variables: { input: { integrationName: PAYNET_US_INTEGRATION_NAME, accountId } } });
      }

      showNotification('PayNet settings saved');
    } catch (err) {
      console.error(err);
      showNotification('Error saving PayNet settings', { type: 'error' });
    }
  };

  const isLoading = paynetCaLoading || paynetUsLoading || vpUpdating || isvInserting || isvDeleting;
  const caEnabled = caForm.watch('enabled');
  const usaEnabled = usaForm.watch('enabled');

  return (
    <Tile>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        PayNet Settings
      </Typography>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && (
        <Stack spacing={3}>
          {/* PayNet Canada */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" fontWeight="medium">
                PayNet Canada
              </Typography>
              <SwitchField name="enabled" label="" control={caForm.control} />
            </Stack>

            {caEnabled && (
              <Stack spacing={2} mt={1}>
                <InputField
                  name="username"
                  label="Username"
                  control={caForm.control}
                  rules={{ required: 'Required' }}
                />
                <InputField
                  name="password"
                  label="Password"
                  control={caForm.control}
                  rules={{ required: 'Required' }}
                  inputProps={{ type: 'password' }}
                />
              </Stack>
            )}
          </Box>

          <Divider />

          {/* PayNet USA */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle1" fontWeight="medium">
                PayNet USA
              </Typography>
              <SwitchField name="enabled" label="" control={usaForm.control} />
            </Stack>

            {usaEnabled && (
              <Stack spacing={2} mt={1}>
                <InputField
                  name="username"
                  label="Username"
                  control={usaForm.control}
                  rules={{ required: 'Required' }}
                />
                <InputField
                  name="password"
                  label="Password"
                  control={usaForm.control}
                  rules={{ required: 'Required' }}
                  inputProps={{ type: 'password' }}
                />
                <InputField
                  name="inquiryMemberNumber"
                  label="Inquiry Member Number"
                  control={usaForm.control}
                  rules={{ required: 'Required' }}
                />
              </Stack>
            )}
          </Box>

          <Box mt={2}>
            <Button color="primary" onClick={handleSave} disabled={isLoading}>
              Save
            </Button>
          </Box>
        </Stack>
      )}
    </Tile>
  );
};
